/**
 * RequestEditModal - Create/Edit requests with tabs for Overview and Attachments
 * 
 * Features:
 * - Pill-style tabs matching established UI pattern
 * - Rich text description with MarkdownEditor
 * - Priority field with color-coded options
 * - Attachments tab with FileList and FileUploader (same pattern as FeatureDetail)
 * - Modal size increased by 100% for better usability
 * - Epic selection with "Create New Epic" option for features
 * - Unsaved changes warning on cancel
 */
import React, { useState, useEffect, useMemo } from 'react';
import { requestsApi, epicsApi, featuresApi, personasApi, goalsApi, initiativesApi, productTagsApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { NotesPanel, AttachmentsPanel } from '../shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Sparkles,
  Layers,
  Users,
  Target,
  Rocket,
  Loader2,
  ArrowRight,
  Plus,
  AlertTriangle,
  FileText,
  Paperclip,
  X,
  Bug,
  Mail,
  User,
  Building2,
  Phone,
  ExternalLink,
  MessageSquare,
  Clock,
  Tag,
  Link2,
  MessageCircle,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { MarkdownEditor } from '../ui/markdown-editor';
import { toast } from 'sonner';

// Type colors and icons - Order: Bug, Feature, Epic, Initiative, Goal, Persona
const TYPE_CONFIG = {
  bug: { color: 'bg-red-500', icon: Bug, label: 'Bug' },
  feature: { color: 'bg-blue-500', icon: Sparkles, label: 'Feature' },
  epic: { color: 'bg-purple-500', icon: Layers, label: 'Epic' },
  initiative: { color: 'bg-orange-500', icon: Rocket, label: 'Initiative' },
  goal: { color: 'bg-yellow-500', icon: Target, label: 'Goal' },
  persona: { color: 'bg-green-500', icon: Users, label: 'Persona' },
};

// Priority options matching CreateFeatureRequestModal
const PRIORITY_OPTIONS = [
  { value: 'Critical', label: 'Critical', color: 'text-red-600' },
  { value: 'High', label: 'High', color: 'text-orange-600' },
  { value: 'Medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'Low', label: 'Low', color: 'text-green-600' },
];

const RequestEditModal = ({ 
  request, 
  productId, 
  isOpen, 
  onClose, 
  onSave,
  onRejectRequest,  // Callback to handle rejection with reason modal
  markStale,  // Callback to invalidate collections after entity creation
  isCommitting = false,
  zIndex = 50,  // Z-index for modal stacking
}) => {
  const { getThemeColors } = useSafeMode();
  const { user } = useAuth();
  const colors = getThemeColors();
  const isEdit = !!request;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'feature',
    title: '',
    description: '',
    stage: 'new',
    consideration_status: null,
    request_data: { priority_level: 'Medium' },
  });
  const [originalData, setOriginalData] = useState(null);
  
  // Epic selection state (for feature commits)
  const [selectedEpicId, setSelectedEpicId] = useState('');
  const [epics, setEpics] = useState([]);
  const [loadingEpics, setLoadingEpics] = useState(false);
  
  // New Epic modal state
  const [showNewEpicModal, setShowNewEpicModal] = useState(false);
  const [newEpicTitle, setNewEpicTitle] = useState('');
  const [newEpicDescription, setNewEpicDescription] = useState('');
  const [creatingEpic, setCreatingEpic] = useState(false);
  
  // Unsaved changes warning
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  
  // Loading states
  const [saving, setSaving] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!originalData) return false;
    return (
      formData.type !== originalData.type ||
      formData.title !== originalData.title ||
      formData.description !== originalData.description ||
      formData.stage !== originalData.stage ||
      formData.consideration_status !== originalData.consideration_status ||
      formData.request_data?.priority_level !== originalData.request_data?.priority_level
    );
  }, [formData, originalData]);
  
  // Load epics when modal opens and type is feature
  useEffect(() => {
    const fetchEpics = async () => {
      if (!productId) return;
      try {
        setLoadingEpics(true);
        const res = await epicsApi.listByProduct(productId);
        setEpics(res.data || []);
      } catch (err) {
        console.error('Error loading epics:', err);
      } finally {
        setLoadingEpics(false);
      }
    };
    
    // Load epics for feature and bug types (both create features under epics)
    if (isOpen && (['feature', 'bug'].includes(formData.type) || isCommitting)) {
      fetchEpics();
    }
  }, [isOpen, productId, formData.type, isCommitting]);
  
  // Reset form when request changes
  useEffect(() => {
    const data = {
      type: request?.type || 'feature',
      title: request?.title || '',
      description: request?.description || '',
      stage: request?.stage || 'new',
      consideration_status: request?.consideration_status || null,
      request_data: request?.request_data || { priority_level: 'Medium' },
    };
    setFormData(data);
    setOriginalData(data);
    setSelectedEpicId('');
    setError(null);
    setActiveTab('overview');
  }, [request]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handlePriorityChange = (value) => {
    setFormData(prev => ({
      ...prev,
      request_data: { ...prev.request_data, priority_level: value }
    }));
  };
  
  // Handle cancel with unsaved changes check
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };
  
  const handleConfirmCancel = () => {
    setShowUnsavedWarning(false);
    onClose();
  };
  
  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    // Check if user is changing status to 'rejected' - need to show rejection reason modal
    const isChangingToRejected = isEdit && 
      formData.stage === 'rejected' && 
      originalData?.stage !== 'rejected';
    
    if (isChangingToRejected && onRejectRequest) {
      // Close this modal and let parent handle the rejection flow
      onClose();
      onRejectRequest(request);
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      if (isEdit) {
        await requestsApi.update(request.request_id, formData);
      } else {
        await requestsApi.create(productId, formData);
      }
      
      onSave();
    } catch (err) {
      console.error('Error saving request:', err);
      setError(err.response?.data?.detail || 'Failed to save request');
    } finally {
      setSaving(false);
    }
  };
  
  // Create new epic
  const handleCreateEpic = async () => {
    if (!newEpicTitle.trim()) return;
    
    try {
      setCreatingEpic(true);
      const res = await epicsApi.createForProduct(productId, {
        title: newEpicTitle,
        description: newEpicDescription,
      });
      
      const newEpic = res.data;
      setEpics(prev => [...prev, newEpic]);
      setSelectedEpicId(newEpic.epic_id);
      setShowNewEpicModal(false);
      setNewEpicTitle('');
      setNewEpicDescription('');
    } catch (err) {
      console.error('Error creating epic:', err);
      setError(err.response?.data?.detail || 'Failed to create epic');
    } finally {
      setCreatingEpic(false);
    }
  };
  
  const handleCancelNewEpic = () => {
    setShowNewEpicModal(false);
    setNewEpicTitle('');
    setNewEpicDescription('');
    setSelectedEpicId('');
  };
  
  const handleCommit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    // Both feature and bug require epic selection
    if (['feature', 'bug'].includes(formData.type) && !selectedEpicId) {
      setError('Please select an Epic');
      return;
    }
    
    try {
      setCommitting(true);
      setError(null);
      
      let entityId = null;
      let entityType = formData.type;
      const priorityLevel = formData.request_data?.priority_level || 'Medium';
      
      console.log(`[RequestEditModal] Creating ${entityType}:`, { 
        title: formData.title, 
        selectedEpicId, 
        productId 
      });
      
      // IMPORTANT: Create entity FIRST, only update/import request on SUCCESS
      // This prevents losing the request if entity creation fails
      switch (entityType) {
        case 'bug':
          // Bug becomes a Feature with "Bug" tag
          if (!selectedEpicId) {
            setError('Epic ID is required to create a bug');
            setCommitting(false);
            return;
          }
          
          // Find or create "Bug" tag
          let bugTagId = null;
          try {
            const tagsRes = await productTagsApi.list(productId);
            const existingBugTag = tagsRes.data?.tags?.find(t => t.name.toLowerCase() === 'bug');
            
            if (existingBugTag) {
              bugTagId = existingBugTag.tag_id;
              console.log(`[RequestEditModal] Found existing Bug tag: ${bugTagId}`);
            } else {
              // Create Bug tag with red color
              const newTagRes = await productTagsApi.create(productId, {
                name: 'Bug',
                color: '#ef4444'
              });
              bugTagId = newTagRes.data.tag_id;
              console.log(`[RequestEditModal] Created Bug tag: ${bugTagId}`);
            }
          } catch (tagErr) {
            console.error('[RequestEditModal] Error with Bug tag:', tagErr);
            // Continue without tag if there's an error
          }
          
          // Create feature with Bug tag
          const bugFeatureRes = await featuresApi.create(selectedEpicId, {
            title: formData.title,
            description: formData.description,
            status: 'Backlog',
            priority_level: priorityLevel,
            tag_ids: bugTagId ? [bugTagId] : [],
          });
          entityId = bugFeatureRes.data.feature_id;
          entityType = 'feature'; // Map to feature for request import
          console.log(`[RequestEditModal] Bug created as Feature: ${entityId} with tag: ${bugTagId}`);
          // Flag features and tags collections for reload
          if (markStale) markStale(['features', 'product_tags']);
          break;
          
        case 'feature':
          if (!selectedEpicId) {
            setError('Epic ID is required to create a feature');
            setCommitting(false);
            return;
          }
          const featureRes = await featuresApi.create(selectedEpicId, {
            title: formData.title,
            description: formData.description,
            status: 'Backlog',
            priority_level: priorityLevel,
          });
          entityId = featureRes.data.feature_id;
          console.log(`[RequestEditModal] Feature created: ${entityId}`);
          // Flag features collection for reload
          if (markStale) markStale(['features']);
          break;
          
        case 'epic':
          const epicRes = await epicsApi.createForProduct(productId, {
            title: formData.title,
            description: formData.description,
          });
          entityId = epicRes.data.epic_id;
          console.log(`[RequestEditModal] Epic created: ${entityId}`);
          // Flag epics collection for reload
          if (markStale) markStale(['epics']);
          break;
          
        case 'persona':
          const personaRes = await personasApi.create(productId, {
            name: formData.title,
            role: formData.title,
            goals: formData.description || '',
            pain_points: '',
            context: '',
          });
          entityId = personaRes.data.persona_id;
          console.log(`[RequestEditModal] Persona created: ${entityId}`);
          // Flag personas collection for reload
          if (markStale) markStale(['personas']);
          break;
          
        case 'goal':
          const goalRes = await goalsApi.create(productId, {
            title: formData.title,
            description: formData.description || '',
          });
          entityId = goalRes.data.goal_id;
          console.log(`[RequestEditModal] Goal created: ${entityId}`);
          // Flag goals collection for reload
          if (markStale) markStale(['goals']);
          break;
          
        case 'initiative':
          const initRes = await initiativesApi.create(productId, {
            title: formData.title,
            description: formData.description || '',
          });
          entityId = initRes.data.initiative_id;
          console.log(`[RequestEditModal] Initiative created: ${entityId}`);
          // Flag initiatives collection for reload
          if (markStale) markStale(['initiatives']);
          break;
          
        default:
          setError(`Unknown entity type: ${entityType}`);
          setCommitting(false);
          return;
      }
      
      // Only update and import request AFTER entity creation succeeds
      // This ensures we don't lose the request if entity creation fails
      if (isEdit && entityId) {
        console.log(`[RequestEditModal] Entity created successfully, now updating request...`);
        await requestsApi.update(request.request_id, formData);
        await requestsApi.import(request.request_id, entityType, entityId);
        console.log(`[RequestEditModal] Request updated and linked to ${entityType}: ${entityId}`);
      }
      
      toast.success(`${typeConfig.label} created successfully`);
      onSave();
    } catch (err) {
      console.error('[RequestEditModal] Error committing request:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create entity';
      setError(`Failed to create ${formData.type}: ${errorMessage}`);
    } finally {
      setCommitting(false);
    }
  };
  
  const typeConfig = TYPE_CONFIG[formData.type] || TYPE_CONFIG.feature;
  const TypeIcon = typeConfig.icon;
  // Bug and Feature both require epic selection
  const canCommit = !['feature', 'bug'].includes(formData.type) || selectedEpicId;
  
  return (
    <>
      {/* Main Request Modal - Custom pattern for proper z-index stacking */}
      {isOpen && (
        <div className="fixed inset-0" style={{ zIndex }}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          
          {/* Modal Content */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={cn("bg-white dark:bg-gray-800 rounded-xl shadow-2xl sm:max-w-[900px] w-full max-h-[90vh] overflow-hidden flex flex-col", colors.bgCard)}>
              {/* Header */}
              <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
                <div className="flex items-center gap-2">
                  <div className={cn("w-3 h-3 rounded-full", typeConfig.color)} />
                  <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                    {isCommitting ? 'Commit Request' : isEdit ? 'Edit Request' : 'New Request'}
                  </h2>
                </div>
                <button
                  onClick={handleCancel}
                  className={cn("p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
          
          {/* Tabs using Shadcn pattern - matching Epic/Feature modals */}
          <div className="px-4 pt-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={cn("mb-2 border inline-flex", colors.bgCard, colors.borderPrimary)}>
                <TabsTrigger 
                  value="overview" 
                  className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  data-testid="tab-overview"
                >
                  Overview
                </TabsTrigger>
                {/* Customer Info tab - #2 position, only for requests sourced from support forms */}
                {isEdit && request?.request_data?.source === 'support_form' && (
                  <TabsTrigger 
                    value="customer" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    data-testid="tab-customer"
                  >
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Customer Info
                  </TabsTrigger>
                )}
                {isEdit && (
                  <TabsTrigger 
                    value="attachments" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    data-testid="tab-attachments"
                  >
                    <Paperclip className="w-3 h-3 mr-1" />
                    Attachments
                  </TabsTrigger>
                )}
                {isEdit && (
                  <TabsTrigger 
                    value="notes" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    data-testid="tab-notes"
                  >
                    <MessageCircle className="w-3 h-3 mr-1" />
                    Notes
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content - Scrollable */}
          <div className="flex-1 overflow-y-auto pt-2 pb-4 px-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            {/* ===== OVERVIEW TAB ===== */}
            {activeTab === 'overview' && (
              <div className="space-y-5">
                {/* Type and Priority Row */}
                <div className="flex gap-4">
                  {/* Type Selection */}
                  <div className="space-y-2 flex-1">
                    <Label>Type</Label>
                    <Select value={formData.type} onValueChange={(v) => handleChange('type', v)}>
                      <SelectTrigger data-testid="request-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_CONFIG).map(([type, config]) => (
                          <SelectItem key={type} value={type}>
                            <div className="flex items-center gap-2">
                              <div className={cn("w-2 h-2 rounded-full", config.color)} />
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Priority */}
                  <div className="space-y-2 flex-1">
                    <Label>Priority</Label>
                    <Select 
                      value={formData.request_data?.priority_level || 'Medium'} 
                      onValueChange={handlePriorityChange}
                    >
                      <SelectTrigger data-testid="request-priority-select">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <span className={option.color}>{option.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Title */}
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Enter request title..."
                    data-testid="request-title-input"
                  />
                </div>
                
                {/* Description with Rich Text Editor */}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <MarkdownEditor
                    content={formData.description}
                    onChange={(value) => handleChange('description', value)}
                    placeholder="Describe the request in detail..."
                    minHeight={180}
                  />
                </div>
                
                {/* Status (only show when editing, not committing) */}
                {isEdit && !isCommitting && (
                  <div className="flex gap-4">
                    <div className="space-y-2 flex-1">
                      <Label>Status</Label>
                      <Select value={formData.stage} onValueChange={(v) => handleChange('stage', v)}>
                        <SelectTrigger data-testid="request-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Inbox (New)</SelectItem>
                          <SelectItem value="considering">Considering</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Stage (when in considering status) */}
                    {formData.stage === 'considering' && (
                      <div className="space-y-2 flex-1">
                        <Label>Board Column</Label>
                        <Select 
                          value={formData.consideration_status || 'needs_definition'} 
                          onValueChange={(v) => handleChange('consideration_status', v)}
                        >
                          <SelectTrigger data-testid="request-column-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="needs_definition">Needs Definition</SelectItem>
                            <SelectItem value="ready_to_rank">Ready to Rank</SelectItem>
                            <SelectItem value="ready_to_commit">Ready to Commit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Commit Info & Epic Selection */}
                {isCommitting && (
                  <>
                    <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4" />
                        <span className="font-medium">Creating {typeConfig.label}</span>
                      </div>
                    </div>
                    
                    {/* Epic Selection for Features and Bugs (Bugs become Features with Bug tag) */}
                    {['feature', 'bug'].includes(formData.type) && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          Epic <span className="text-destructive">*</span>
                        </Label>
                        <Select 
                          value={selectedEpicId} 
                          onValueChange={(v) => {
                            if (v === '__new__') {
                              setShowNewEpicModal(true);
                            } else {
                              setSelectedEpicId(v);
                            }
                          }}
                          disabled={loadingEpics}
                        >
                          <SelectTrigger className={!selectedEpicId ? "border-amber-500" : ""}>
                            <SelectValue placeholder={loadingEpics ? "Loading epics..." : "Select an Epic"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__new__" className="text-blue-600 font-medium">
                              <div className="flex items-center gap-2">
                                <Plus className="h-3 w-3" />
                                Create New Epic
                              </div>
                            </SelectItem>
                            {epics.map(epic => (
                              <SelectItem key={epic.epic_id} value={epic.epic_id}>
                                {epic.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!selectedEpicId && (
                          <p className="text-xs text-amber-600">
                            {formData.type === 'bug' 
                              ? "Bugs are created as Features with a 'Bug' tag. Select an Epic above."
                              : "Features must belong to an Epic. Select one above or create a new one."
                            }
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* ===== ATTACHMENTS TAB ===== */}
            {activeTab === 'attachments' && isEdit && (
              <div className="min-h-[400px]">
                <AttachmentsPanel
                  entityType="request"
                  entityId={request?.request_id}
                  productId={productId}
                  canEdit={true}
                  colors={colors}
                />
              </div>
            )}
            
            {/* ===== NOTES TAB ===== */}
            {activeTab === 'notes' && isEdit && request?.request_id && (
              <div className="min-h-[400px]">
                <NotesPanel
                  entityType="request"
                  entityId={request.request_id}
                  teamMembers={[]}
                  colors={colors}
                />
              </div>
            )}
            
            {/* ===== CUSTOMER INFO TAB ===== */}
            {activeTab === 'customer' && isEdit && request?.request_data?.source === 'support_form' && (
              <div className="min-h-[400px] space-y-6">
                {/* Source Badge with Form Link */}
                <div className={cn("p-4 rounded-lg border", colors.border, "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800")}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="font-medium text-blue-900 dark:text-blue-100">Sourced from Customer Feedback Form</span>
                    </div>
                    {request?.request_data?.form_id && (
                      <a
                        href={`/products/${request.product_id}?tab=ideation&space=support-forms&form=${request.request_data.form_id}${request.request_data.submission_id ? `&submission=${request.request_data.submission_id}` : ''}`}
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        title="Opens the source form. If the original submission was dismissed, you'll be notified."
                      >
                        <Link2 className="h-3 w-3" />
                        View Source Form
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    This request was created from a public feedback form submission. All customer data has been preserved on this request.
                  </p>
                </div>

                {/* Submission Details */}
                <div className="space-y-4">
                  <h4 className={cn("font-medium flex items-center gap-2", colors.textPrimary)}>
                    <Clock className="h-4 w-4" />
                    Submission Details
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Submitted At */}
                    <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-500">Submitted</span>
                      </div>
                      <p className={colors.textPrimary}>
                        {new Date(request.created_at).toLocaleString(undefined, {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    
                    {/* Tags from Form */}
                    {request?.tags?.length > 0 && (
                      <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Source Tags</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {request.tags.map((tag, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className={cn("font-medium flex items-center gap-2", colors.textPrimary)}>
                    <User className="h-4 w-4" />
                    Contact Information
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Email */}
                    {request?.request_data?.submitter_email && (
                      <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Email</span>
                        </div>
                        <a 
                          href={`mailto:${request.request_data.submitter_email}`}
                          className="text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {request.request_data.submitter_email}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                    
                    {/* Name */}
                    {request?.request_data?.submitter_name && (
                      <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Name</span>
                        </div>
                        <p className={colors.textPrimary}>{request.request_data.submitter_name}</p>
                      </div>
                    )}
                    
                    {/* Company */}
                    {request?.request_data?.submitter_company && (
                      <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Company</span>
                        </div>
                        <p className={colors.textPrimary}>{request.request_data.submitter_company}</p>
                      </div>
                    )}
                    
                    {/* Phone */}
                    {request?.request_data?.submitter_phone && (
                      <div className={cn("p-4 rounded-lg border", colors.border, colors.cardBg)}>
                        <div className="flex items-center gap-2 mb-1">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Phone</span>
                        </div>
                        <a 
                          href={`tel:${request.request_data.submitter_phone}`}
                          className="text-blue-600 hover:underline"
                        >
                          {request.request_data.submitter_phone}
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* No contact info available message */}
                  {!request?.request_data?.submitter_email && 
                   !request?.request_data?.submitter_name && 
                   !request?.request_data?.submitter_company && 
                   !request?.request_data?.submitter_phone && (
                    <div className={cn("p-6 text-center rounded-lg border border-dashed", colors.border)}>
                      <User className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">No contact information was provided with this submission.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className={cn("flex justify-end gap-2 px-6 py-4 border-t", colors.borderPrimary)}>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            {isCommitting ? (
              <Button 
                onClick={handleCommit} 
                disabled={committing || !canCommit}
                title={!canCommit ? "Please select an Epic first" : ""}
                data-testid="commit-request-btn"
              >
                {committing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Create {typeConfig.label}
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={handleSave} 
                disabled={saving}
                data-testid="save-request-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  isEdit ? 'Save Changes' : 'Create Request'
                )}
              </Button>
            )}
          </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Unsaved Changes Warning Dialog - Custom pattern */}
      {showUnsavedWarning && (
        <div className="fixed inset-0" style={{ zIndex: zIndex + 10 }}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={cn("bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6", colors.bgCard)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-amber-500/10">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Unsaved Changes</h3>
              </div>
              <p className={cn("mb-6", colors.textSecondary)}>
                You have unsaved changes. Are you sure you want to close? Your changes will be lost.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowUnsavedWarning(false)}>
                  Keep Editing
                </Button>
                <Button 
                  onClick={handleConfirmCancel} 
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Discard Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* New Epic Creation Dialog - Custom pattern */}
      {showNewEpicModal && (
        <div className="fixed inset-0" style={{ zIndex: zIndex + 10 }}>
          <div className="absolute inset-0 bg-black/60" onClick={handleCancelNewEpic} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={cn("bg-white dark:bg-gray-800 rounded-xl shadow-2xl sm:max-w-[450px] w-full", colors.bgCard)}>
              {/* Header */}
              <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-purple-500" />
                  <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Create New Epic</h2>
                </div>
                <button
                  onClick={handleCancelNewEpic}
                  className={cn("p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
          
              <div className="space-y-4 p-6">
                <div className="space-y-2">
                  <Label>Epic Title <span className="text-destructive">*</span></Label>
                  <Input
                    value={newEpicTitle}
                    onChange={(e) => setNewEpicTitle(e.target.value)}
                    placeholder="Enter epic title..."
                    autoFocus
                  />
                </div>
            
                <div className="space-y-2">
                  <Label>Description</Label>
                  <MarkdownEditor
                    content={newEpicDescription}
                    onChange={setNewEpicDescription}
                    placeholder="Describe the epic..."
                    minHeight={100}
                  />
                </div>
              </div>
          
              {/* Footer */}
              <div className={cn("flex justify-end gap-2 px-6 py-4 border-t", colors.borderPrimary)}>
                <Button variant="outline" onClick={handleCancelNewEpic}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateEpic} 
                  disabled={creatingEpic || !newEpicTitle.trim()}
                >
                  {creatingEpic ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Epic
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestEditModal;
